<template>
  <div>
    <!-- Section Header -->
    <div class="text-center mb-4">
      <h2 class="fw-bold text-primary" style="font-size: 1.75rem; letter-spacing: 0.5px;">
        🎓 Detailed Student View
      </h2>
      <p class="text-muted" style="font-size: 0.95rem;">
        Overview of student applications and their respective responses
      </p>
    </div>

    <b-col class="text-left mb-2">
      <b-button class="m-1" @click="prepareExport()" variant="primary">Export to Excel</b-button>
   
      <b-button class="m-1" @click="$bvModal.show('download-type-modal')" variant="danger">Download</b-button>
    </b-col>

    <b-modal
      id="download-type-modal"
      title="Download Admission Forms"
      ok-title="Download"
      @ok="downloadAdmissionformFullDetails()"
      cancel-title="Cancel"
    >
      <b-form-group>
        <b-form-radio v-model="selectedExportType" value="single_file" class="mb-3">
          <div>All students in one PDF</div>
          <div class="text-muted" style="font-size: 0.85rem;">Exports all student submissions as a single PDF file.</div>
        </b-form-radio>

        <b-form-radio v-model="selectedExportType" value="all_students_individual" class="mb-3">
          <div>Each student as a separate PDF (ZIP)</div>
          <div class="text-muted" style="font-size: 0.85rem;">Generates one PDF per student, bundled into a ZIP archive.</div>
        </b-form-radio>

        <b-form-radio v-model="selectedExportType" value="single_file_with_attachment" class="mb-3">
          <div>All students with attachments in one PDF</div>
          <div class="text-muted" style="font-size: 0.85rem;">Includes uploaded attachments alongside answers in a single PDF.</div>
        </b-form-radio>

        <b-form-radio v-model="selectedExportType" value="all_students_individual_with_attachment">
          <div>Each student with attachments, individual (ZIP)</div>
          <div class="text-muted" style="font-size: 0.85rem;">One PDF per student including attachments, bundled into a ZIP archive.</div>
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <b-modal
      id="student-download-modal"
      title="Download Student Form"
      ok-title="Download"
      @ok="downloadStudentPdf()"
      cancel-title="Cancel"
    >
      <b-form-group>
        <b-form-radio v-model="studentDownloadType" value="single_file" class="mb-2">
          <div>PDF</div>
          <div class="text-muted" style="font-size: 0.85rem;">Form only, without attachments.</div>
        </b-form-radio>
        <b-form-radio v-model="studentDownloadType" value="single_file_with_attachment">
          <div>PDF + Attachment</div>
          <div class="text-muted" style="font-size: 0.85rem;">Form merged with uploaded PDF attachments.</div>
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <b-modal id="delete-application-modal" title="Confirm Deletion" hide-footer centered no-close-on-backdrop>
      <div class="text-danger mb-3">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        <strong>Warning:</strong> This application and all submitted answers will be permanently deleted after 24 hours. This action cannot be undone.
      </div>
      <p class="mb-2">Type <strong>DELETE</strong> below to confirm:</p>
      <b-form-input v-model="deleteConfirmText" placeholder="Type DELETE to confirm" />
      <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
        <b-button variant="secondary" size="sm" @click="$bvModal.hide('delete-application-modal')">Cancel</b-button>
        <b-button variant="danger" size="sm" :disabled="deleteConfirmText !== 'DELETE'" @click="confirmDeleteApplication">Delete</b-button>
      </div>
    </b-modal>

    <div class="table-responsive rounded">
     <table class="table table-striped table-bordered" id="institutionUserDetails"
        style="width: max-content; min-width: 100%;">
        <thead class="thead-dark text-left">
          <!-- First row: Section Names -->
          <tr>
            <!-- Sl No -->
            <th rowspan="2" style="padding: 10px; border: 1px solid #ddd; min-width: 60px;">
              Sl No
            </th>

            <!-- Student Name -->
            <th rowspan="2" style="padding: 10px; border: 1px solid #ddd;">
              Student Name
            </th>

            <th v-for="section in admissionFormDetails.admissionformsections" :key="'section-' + section.id"
              :colspan="sectionColspan(section)" class="text-center"
              style="padding: 10px; border: 1px solid #ddd;">
              {{ section.name }}
            </th>

            <th rowspan="2" style="padding: 10px; border: 1px solid #ddd; min-width: 140px;">
              Actions
            </th>
          </tr>

          <!-- Second row: Questions inside each section -->
          <tr>
            <template v-for="section in admissionFormDetails.admissionformsections">
              <!-- Payment collection sections have no questions, just one status column -->
              <th v-if="isPaymentSection(section)" :key="'payment-header-' + section.id" class="question-header-cell">
                Payment Status
              </th>
              <template v-else>
                <th v-for="question in section.admissionformquestions" :key="'question-' + section.id + '-' + question.columnKey"
                  class="question-header-cell" v-b-popover.hover.top="question.question_no + ') ' + question.name">
                  <div class="question-header-text">
                    {{ question.question_no }}) {{ question.name }}
                  </div>
                </th>
              </template>
            </template>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(application, index) in admissionFormDetails.applications" :key="'app-' + application.id"
            :style="application.deleted_at ? 'opacity: 0.6; background: #fff5f5;' : ''">

            <!-- Sl No -->
            <td style="padding: 8px 10px; border: 1px solid #ddd; text-align: center;">
              {{ index + 1 }}
            </td>

            <!-- Student info -->
            <td style="padding: 8px 10px; border: 1px solid #ddd;">
              <div class="d-flex flex-column">
                <div class="d-flex align-items-center mb-1" style="gap: 6px;">
                  <input v-if="adminrole_admissiondesk && application.verified" type="checkbox"
                    v-model="selectedStudents" :value="{
                      id: application.id,
                      name: application.name,
                      email: application.email
                    }" @change="toggleGoToClassButton" style="transform: scale(0.9);" />

                  <span class="fw-semibold text-dark" style="font-size: 0.85rem;">
                    {{ application.name }}
                  </span>
                  <i class="fas fa-download text-success ml-1" style="cursor: pointer; font-size: 0.85rem;"
                    @click="openStudentDownloadModal(application)" title="Download PDF"></i>
                </div>
              </div>
            </td>

            <!-- Student answers grouped by section/questions -->
            <template v-for="section in admissionFormDetails.admissionformsections">
              <!-- Payment collection section: paid amount (and category) or "Not Paid" -->
              <td v-if="isPaymentSection(section)" :key="'payment-' + application.id + '-' + section.id"
                style="padding: 10px; border: 1px solid #ddd;">
                <span v-if="getPayment(application, section)">
                  Paid - Rs. {{ getPayment(application, section).amount }}<template
                    v-if="getPayment(application, section).payment_category"> ({{ getPayment(application, section).payment_category.name }})</template>
                </span>
                <span v-else class="text-muted">Not Paid</span>
              </td>
              <template v-else>
                <td v-for="question in section.admissionformquestions"
                  :key="'answer-' + application.id + '-' + section.id + '-' + question.columnKey"
                  style="padding: 10px; border: 1px solid #ddd;">
                  {{ getColumnAnswer(application.answers, question) }}

                  <div v-if="question.dependentPart !== 'sub' && getUploads(application.answers, question.id).length" class="mt-2">
                    <div class="uploaded-files">
                      <div v-for="(file, index) in getUploads(application.answers, question.id)"
                        :key="'file-' + application.id + '-' + question.id + '-' + index" class="file-item">
                        <a :href="userAttachments + file.file_path + file.file_name" target="_blank" class="file-link"
                          :title="file.file_name">
                          {{ formatFileName(file.file_name) }}
                        </a>
                      </div>
                    </div>
                  </div>
                </td>
              </template>
            </template>

            <!-- Actions cell -->
            <td style="padding: 8px 10px; border: 1px solid #ddd; vertical-align: middle; min-width: 140px;">
              <div v-if="!application.deleted_at">
                <i class="fas fa-trash text-danger" style="cursor: pointer; font-size: 0.9rem;"
                  @click="deleteApplication(application.id)"></i>
              </div>
              <div v-else style="display: inline-flex; flex-direction: column; width: 100%;">
                <span class="badge badge-danger text-light mb-1"
                  style="font-size: 10px; width: 100%; text-align: center;">
                  Delete in: {{ countdowns[application.id] }}
                </span>
                <b-button size="sm" variant="success" style="font-size: 10px; width: 100%;"
                  @click="cancelApplicationDeletion(application.id)">
                  Cancel Deletion
                </b-button>
              </div>
            </td>

          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import XLSX from "xlsx";
import moment from "moment";
import { splitDependentAnswer } from "./dependentDropdown";
export default {
  props: {
    initialFormId: { default: null },
  },
  computed: {
    activeFormId() {
      return this.initialFormId || this.$route.params.admissionform_id;
    },
  },
  data() {
    return {
      moment,
      admissionFormDetails: { admissionformquestions: [], applications: [] },
      newQuestionsAnswerAttachments: {},
      uploadPercentage: {},
      userAttachments: null,
      selectedStudent: {},
      selectedExportType: 'single_file',
      studentDownloadType: 'single_file',
      selectedStudentForDownload: null,
      adminrole_admissiondesk: false,
      countdowns: {},
      countdownInterval: null,
      deleteConfirmText: '',
      pendingDeleteApplicationId: null,
    };
  },
  beforeDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  },
  async created() {

    this.userAttachments = this.$store.getters.getAPIKey.userAttachments;
    await this.getAdmissionFormDetails();
  },
  methods: {
 
    async downloadAdmissionformFullDetails() {
      this.loadReport = true;
      const url =
        this.$store.getters.getAPIKey.mainAPI +
        `/admissionforms/${this.activeFormId}/pdfexport`;

      const isZip = [
        'all_students_individual',
        'all_students_individual_with_attachment',
      ].includes(this.selectedExportType);

      const blobType = isZip ? 'application/zip' : 'application/pdf';
      const fileName = isZip ? 'Admissionforms.zip' : 'Admissionform.pdf';

      await this.$axios
        .post(url, { type: this.selectedExportType }, { responseType: 'blob' })
        .then((response) => {
          const fileBlob = new Blob([response.data], { type: blobType });
          const fileUrl = window.URL.createObjectURL(fileBlob);
          const link = document.createElement('a');
          link.href = fileUrl;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch((error) => {
          let errorText = 'Failed to Download, please check connectivity';
          if (error.response != undefined)
            errorText =
              error.response.status == 422
                ? 'Custom report is not configured by admin'
                : 'Failed to Download, please try again';
          this.$toasted.error(errorText, { duration: 3000 });
        });
      this.loadReport = false;
    },

    openStudentDownloadModal(application) {
      this.selectedStudentForDownload = application;
      this.studentDownloadType = 'single_file';
      this.$bvModal.show('student-download-modal');
    },

    async downloadStudentPdf() {
      const application = this.selectedStudentForDownload;
      const url = this.$store.getters.getAPIKey.mainAPI +
        `/admissionforms/${this.activeFormId}/applications/${application.id}/pdfexport`;
      const safeName = (application.name || application.id).toString().replace(/[^A-Za-z0-9_-]/g, '_');

      await this.$axios
        .post(url, { type: this.studentDownloadType }, { responseType: 'blob' })
        .then((response) => {
          const fileBlob = new Blob([response.data], { type: 'application/pdf' });
          const fileUrl = window.URL.createObjectURL(fileBlob);
          const link = document.createElement('a');
          link.href = fileUrl;
          link.setAttribute('download', safeName + '.pdf');
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.$toasted.error('Failed to download, please try again', { duration: 3000 });
        });
    },

    async prepareExport() {
      this.edit = true;
      this.loadExams = true;
      await setTimeout(() => {
        this.exportToExcel("xlsx");
        this.edit = false;
        this.loadExams = false;
      }, 3000);
    },

    async exportToExcel(type, fn, dl) {
      var elt = document.getElementById("institutionUserDetails");
      var wb = await XLSX.utils.table_to_book(elt, { sheet: "Sheet JS" });
      this.loadExams = false;

      dl
        ? XLSX.write(wb, { bookType: type, bookSST: true, type: "base64" })
        : XLSX.writeFile(wb, fn || "admisssionform." + (type || "xlsx"));
    },

    formatFileName(name) {
      // Optional: trim or shorten long names
      if (name.length > 30) {
        return name.slice(0, 25) + '...';
      }
      return name;
    },

    newFileInputMask(questionId) {
      let elementReference = `${"newFileInput" + questionId}`;
      this.$refs[elementReference][0].$refs.input.click();
    },
    async newFileInput(file, questionId) {
      this.$set(this.newQuestionsAnswerAttachments, questionId, []);
      this.newQuestionsAnswerAttachments[questionId].push(file.name);

      this.$set(this.uploadPercentage, questionId, {});
      this.$set(this.uploadPercentage[questionId], "value", 0);

      let index = this.newQuestionsAnswerAttachments[questionId].indexOf(
        file.name
      );

      await this.uploadAttachment(file[0], questionId);

      await this.newQuestionsAnswerAttachments[questionId].splice(index, 1);

      this.getUplodedAnswer(questionId);
      // // this.initiateAdmissionForm();
    },
    getAnswer(answers, questionId) {
      return answers?.find(ans => ans.question_id === questionId)?.answer || "";
    },
    // a dependent drop-down shows its main and sub parts in separate columns
    getColumnAnswer(answers, question) {
      const answer = this.getAnswer(answers, question.id);
      if (!question.dependentPart || !answer) return answer;
      const { main, sub } = splitDependentAnswer(question, answer);
      if (question.dependentPart === "main") return main || answer;
      return main ? sub || "" : "";
    },

    getUploads(answers, questionId) {
      return answers?.find(ans => ans.question_id === questionId)?.files || [];
    },

    isPaymentSection(section) {
      return section.type === 'payment_collection';
    },

    // Payment sections show a single status column, other sections one column per question
    sectionColspan(section) {
      return this.isPaymentSection(section) ? 1 : section.admissionformquestions.length;
    },

    getPayment(application, section) {
      return application.payments.find(p => p.admissionform_section_id === section.id) || null;
    },

    startCountdowns() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      const GRACE_MS = 24 * 60 * 60 * 1000;
      const tick = () => {
        const updated = {};
        (this.admissionFormDetails.applications || []).forEach(app => {
          if (!app.deleted_at) return;
          const remaining = GRACE_MS - (Date.now() - new Date(app.deleted_at).getTime());
          if (remaining <= 0) return;
          const h = Math.floor(remaining / 3600000);
          const m = Math.floor((remaining % 3600000) / 60000);
          const s = Math.floor((remaining % 60000) / 1000);
          updated[app.id] = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        });
        this.countdowns = updated;
      };
      tick();
      this.countdownInterval = setInterval(tick, 1000);
    },
    deleteApplication(applicationId) {
      this.pendingDeleteApplicationId = applicationId;
      this.deleteConfirmText = '';
      this.$bvModal.show('delete-application-modal');
    },
    async confirmDeleteApplication() {
      this.$bvModal.hide('delete-application-modal');
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/applications/${this.pendingDeleteApplicationId}`;
      try {
        await this.$axios.delete(url);
        this.$toast.success('Application delete initiated', { position: 'top', duration: 3000 });
        await this.getAdmissionFormDetails();
      } catch {
        this.$toast.error('Failed to delete application', { position: 'top', duration: 3000 });
      }
      this.pendingDeleteApplicationId = null;
    },
    async cancelApplicationDeletion(applicationId) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Restore this application?', {
        title: 'Cancel Deletion',
        okVariant: 'success',
        okTitle: 'Restore',
        cancelTitle: 'No',
        centered: true,
      });
      if (!confirmed) return;
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/applications/${applicationId}/restore`;
      try {
        await this.$axios.patch(url);
        this.$toast.success('Application restored', { position: 'top', duration: 3000 });
        await this.getAdmissionFormDetails();
      } catch {
        this.$toast.error('Failed to restore application', { position: 'top', duration: 3000 });
      }
    },
    async getAdmissionFormDetails() {
      const admissionformId = this.activeFormId;
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/${admissionformId}`;

      try {
        const { data } = await this.$axios.get(url);

        this.admissionFormDetails = {
          ...data,

          // manual verification sections are only printed in the PDF; there are no answers to show
          admissionformsections: data.admissionformsections.filter(section => section.type !== 'manual_verification').map(section => ({
            ...section,
            admissionformquestions: section.questions.reduce((columns, q) => {
              let choices = q.input_attributes[0]?.dropdown
                ? [{ value: null, text: "Select One" }]
                : [];

              // dependent drop-down keeps its options as an object, not a plain choice list
              if (Array.isArray(q.answers) && q.answers.length !== 0) {
                choices.push(
                  ...q.answers.map(choice => ({
                    value: choice,
                    text: choice
                  }))
                );
              }

              const column = {
                id: q.id,
                columnKey: String(q.id),
                name: q.name,
                question_no: q.question_no,
                input_attributes: q.input_attributes,
                answers: q.answers,
                choices: choices,
                allow_attachment: q.allow_attachment == 1,
              };
              columns.push(column);

              // dependent drop-down: the sub question gets its own column, both read from the one "Main / Sub" answer
              if (q.input_attributes[0]?.dependentDropdown && q.answers?.sub_question) {
                column.dependentPart = "main";
                columns.push({
                  ...column,
                  columnKey: q.id + "-sub",
                  dependentPart: "sub",
                  name: q.answers.sub_question,
                  question_no: q.answers.sub_question_no || q.question_no + ".1",
                  allow_attachment: false,
                });
              }
              return columns;
            }, []),
          })),

          applications: data.applications
            .filter(app => {
              if (app.deleted_at) {
                return (Date.now() - new Date(app.deleted_at).getTime()) < 24 * 60 * 60 * 1000;
              }
              return app.end_at !== null;
            })
            .map(app => {
              const answers = app.answers?.map(ans => ({
                question_id: ans.question_id,
                answer: ans.answer,
                files: ans.files || [],
                verified_at: ans.verified_at,
                verified_by: ans.verifier
                  ? `${ans.verifier.first_name} ${ans.verifier.last_name}`
                  : null,
                modification_details: ans.modification_details,
              })) || [];

              // Check if all answers are verified
              const verified =
                answers.length > 0 && answers.every(ans => ans.verified_at !== null);

              return {
                id: app.id,
                name: app.name,
                email: app.email,
                end_at: app.end_at,
                usergroup: app.usergroup,
                answers: answers,
                payments: app.payments || [],
                verified: verified,
                deleted_at: app.deleted_at || null,
                deleted_by: app.deleted_by || null,
              };
            }),
        };
        this.startCountdowns();
      } catch (e) {
        console.error("Error fetching admission form:", e);
      }
    },
  },
};
</script>
<style scoped>
.uploaded-files {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-item {
  font-size: 14px;
}

.file-link {
  color: #007bff;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;
}

.file-link:hover {
  text-decoration: underline;
  color: #0056b3;
}

.file-link i {
  color: #555;
}

.question-header-cell {
  padding: 10px;
  border: 1px solid #ddd;
  max-width: 500px;
  vertical-align: middle;
}

.question-header-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}
</style>